import {
	ChangeEvent,
	Dispatch,
	FC,
	FocusEventHandler,
	MouseEvent,
	ReactNode,
	SetStateAction,
	useEffect,
	useRef,
	useState,
} from 'react';
import { Chip, CircularProgress, FormControl, MenuItem, Paper, Popover, styled } from '@mui/material';
import InfiniteScroll from 'react-infinite-scroll-component';

import Text from '../Text';
import { PEARL, TEXT } from '../../Helpers/colors';
import { Close } from '../../Assets/svgIcons';
import { CustomizedInput } from '../Input';

export const StyledChip = styled(Chip)(() => ({
	display: 'flex',
	alignIitems: 'center',
	gap: '4px',
	borderRadius: '999px',
	background: PEARL.TERTIARY,
	color: TEXT.SECONDARY,
	cursor: 'pointer',
	textAlign: 'center',
	fontFamily: 'Inter',
	fontSize: '12px',
	fontStyle: 'normal',
	fontWeight: 500,
	lineHeight: '16px',
}));

export interface SelectDataProperties {
	id: string;
	label: string;
}

interface SelectProperties {
	className?: string;
	clearData?: () => void;
	data: SelectDataProperties[];
	description?: string;
	disabled?: boolean;
	error?: boolean;
	errorMessage?: string;
	hasMore?: boolean;
	label: string;
	loading?: boolean;
	onBlur?: FocusEventHandler<HTMLInputElement>;
	placeholder?: string;
	searchString: string;
	setSearchString: (value: string) => void;
	setSize?: Dispatch<SetStateAction<number>>;
	setValue: (value: string) => void;
	startAdornment?: ReactNode;
}

const SearchSelect: FC<SelectProperties> = ({
	label,
	className,
	data,
	setValue,
	hasMore = false,
	loading,
	searchString,
	setSearchString,
	setSize = () => null,
	clearData = () => null,
	startAdornment,
	placeholder,
}) => {
	const inputRef = useRef<HTMLInputElement | null>(null);
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [inputWidth, setInputWidth] = useState<number>(0);

	const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;

		if (!anchorEl && inputRef?.current) {
			inputRef.current.click();
		}
		setSearchString(value);
	};

	const handleOpen = (event: MouseEvent<HTMLElement>) => {
		if (inputRef.current) {
			setInputWidth(inputRef.current.clientWidth);
		}
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleLoadMore = () => {
		if (!hasMore || !setSize) return;
		setSize((prev) => prev + 8);
	};

	const handleChoose = (item: SelectDataProperties) => {
		const selectedLabel = item.label;
		setSearchString(selectedLabel);
		setValue(item.id);
		handleClose();
	};

	const handleClearSearch = () => {
		setSearchString('');
		setValue('');
		handleClose();
		clearData();
	};

	return (
		<FormControl style={{ width: '100%' }} ref={inputRef}>
			<Text
				className={`inputLabel ${label ? 'active' : ''}`}
				title={label}
				type="psm"
				fontWeight="medium"
				color={loading ? TEXT.QUATERNARY : TEXT.PRIMARY}
			/>
			<CustomizedInput
				value={searchString}
				onChange={handleInputChange}
				onClick={handleOpen}
				className={className}
				disabled={loading}
				endAdornment={
					Boolean(searchString) && <Close onClick={handleClearSearch} style={{ fontSize: '12px', cursor: 'pointer' }} />
				}
				startAdornment={startAdornment}
				placeholder={placeholder}
			/>
			<Popover
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={handleClose}
				disableAutoFocus
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'left',
				}}
			>
				<Paper
					style={{
						maxHeight: '350px',
						overflowY: 'auto',
						boxShadow: 'none',
						width: inputWidth,
					}}
				>
					<InfiniteScroll
						dataLength={data.length}
						hasMore={Boolean(hasMore)}
						next={handleLoadMore}
						loader={
							<div style={{ textAlign: 'center', color: TEXT.PRIMARY }}>
								<CircularProgress size={24} color="inherit" />
							</div>
						}
						scrollableTarget="scrollable-popover-1"
						height={data.length > 8 ? 300 : 'max-content'}
					>
						{/* {anchorEl && (
              <MenuItem
                style={{ position: "absolute", top: 0, right: 0, zIndex: 1500 }}
                onClick={handleClose}
              >
                <Close />
              </MenuItem>
            )} */}
						{Boolean(data) && Boolean(data.length) ? (
							data.map((item, index) => (
								<MenuItem key={index} onClick={() => handleChoose(item)}>
									<Text title={item.label} type="psm" />
								</MenuItem>
							))
						) : (
							<MenuItem style={{ justifyContent: 'center' }}>
								<Text title="No Results" fontWeight="semiBold" type="psm" />
							</MenuItem>
						)}
					</InfiniteScroll>
				</Paper>
			</Popover>
		</FormControl>
	);
};

interface MultipleSearchSelectProperties {
	className?: string;
	data: SelectDataProperties[];
	description?: string;
	disabled?: boolean;
	error?: boolean;
	errorMessage?: string;
	hasMore?: boolean;
	label: string;
	loading?: boolean;
	onBlur?: FocusEventHandler<HTMLInputElement>;
	placeholder?: string;
	searchString: string;
	setPageSize?: Dispatch<SetStateAction<number>>;
	setSearchString: (value: string) => void;
	setValues: (value: string[]) => void;
	values: string[];
	width?: string;
}

export const MultipleSearchSelect: FC<MultipleSearchSelectProperties> = ({
	label,
	className,
	data,
	values,
	setValues,
	hasMore,
	setPageSize,
	loading,
	searchString,
	setSearchString,
	width = '300px',
}) => {
	const inputRef = useRef<HTMLInputElement | null>(null);
	const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
	const [selected, setSelected] = useState<SelectDataProperties[]>([]);

	const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;

		if (!anchorEl && inputRef?.current) {
			inputRef.current.click();
		}
		setSearchString(value);
	};

	const handleOpen = (event: MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleClose = () => {
		setAnchorEl(null);
	};

	const handleFocusInput = () => {
		if (inputRef.current && !anchorEl) {
			inputRef.current.click();
		}
	};

	const handleLoadMore = () => {
		if (!hasMore) return;
		setPageSize?.((prev) => prev + 8);
	};

	const handleChoose = (item: SelectDataProperties) => {
		setSearchString('');

		if (values.includes(item.id)) return;
		setValues([...values, item.id]);
		setSelected((prev) => [...prev, item]);
		handleFocusInput();
	};

	const removeItemHandler = (id: string) => {
		if (!values.includes(id)) return;
		setValues(values.filter((x) => x !== id));
		setSelected((prev) => prev.filter((x) => x.id !== id));
	};

	const clearAllHandler = () => {
		setValues([]);
		setSelected([]);
		setSearchString('');
	};

	useEffect(() => {
		if (values.length === selected.length) return;
		const selectedData = data.filter((x) => values.includes(x.id));
		setSelected(selectedData);
	}, [values, data, selected]);

	return (
		<FormControl style={{ width }}>
			<Text
				className={`inputLabel ${label ? 'active' : ''}`}
				title={label}
				type="psm"
				fontWeight="medium"
				color={loading ? TEXT.QUATERNARY : TEXT.PRIMARY}
			/>
			<CustomizedInput
				placeholder="Search"
				value={searchString}
				onChange={handleInputChange}
				onClick={handleOpen}
				className={className}
				disabled={loading}
				style={{
					display: 'flex',
					flexDirection: 'column',
					height: 'max-content',
				}}
				endAdornment={
					Boolean(selected.length) && (
						<div
							style={{
								display: 'flex',
								alignItems: 'center',
								gap: '8px',
								width: '100%',
							}}
						>
							<div
								style={{
									display: 'inline-flex',
									flexWrap: 'wrap',
									gap: 8,
									width: 'max-content',
								}}
							>
								{selected.map((value) => (
									<StyledChip
										key={value.id}
										label={value.label}
										className="marginBottom8"
										onDelete={() => removeItemHandler(value.id)}
										deleteIcon={<Close style={{ marginRight: '8px', fontSize: '16px' }} />}
									/>
								))}
							</div>
							<Close onClick={clearAllHandler} />
						</div>
					)
				}
				inputRef={inputRef}
			/>

			<Popover
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				onClose={handleClose}
				disableAutoFocus
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'left',
				}}
				slotProps={{
					paper: {
						id: 'scrollable-popover-1',
						style: {
							height: data.length > 8 ? '300px' : 'max-content',
							width,
							overflow: 'scroll',
							overflowX: 'hidden',

							overflowY: 'hidden',
							boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
						},
					},
				}}
			>
				<Paper>
					{Boolean(data) && Boolean(data.length) ? (
						<InfiniteScroll
							dataLength={data.length}
							hasMore={Boolean(hasMore)}
							next={handleLoadMore}
							loader={
								<div style={{ textAlign: 'center', color: TEXT.PRIMARY }}>
									<CircularProgress size={24} color="inherit" />
								</div>
							}
							scrollableTarget="scrollable-popover-1"
							height={data.length > 8 ? 300 : 'max-content'}
						>
							{data.map((item, index) => (
								<MenuItem key={index} onClick={() => handleChoose(item)}>
									<Text title={item.label} type="psm" />
								</MenuItem>
							))}
						</InfiniteScroll>
					) : (
						<MenuItem style={{ justifyContent: 'center' }}>
							<Text title="No Results" fontWeight="semiBold" type="psm" />
						</MenuItem>
					)}
				</Paper>
			</Popover>
		</FormControl>
	);
};

export default SearchSelect;
